import React from "react";
import { X, Check, XCircle } from "lucide-react";

export default function CTRFixPreview({ insight, onApprove, onReject, onClose }) {
  if (!insight || !insight.suggested_fix) return null;

  const fix = insight.suggested_fix;
  const isContentBoost = insight.suggested_fix_type === "content_boost";

  const fields = [];

  // Both types show title, meta, focus keyword
  fields.push(
    { label: "Title Tag", current: fix.current_title, suggested: fix.suggested_title },
    { label: "Meta Description", current: fix.current_description, suggested: fix.suggested_description },
    { label: "Focus Keyword", current: fix.current_focus_keyword, suggested: fix.suggested_focus_keyword },
  );

  // Content boost also shows the paragraph
  if (isContentBoost && fix.content_paragraph) {
    fields.push({
      label: "Content",
      current: null,
      suggested: fix.content_paragraph,
      isContentBlock: true,
    });
  }

  // CTR optimize may show schema
  if (!isContentBoost && fix.suggested_schema) {
    fields.push({
      label: "Schema (FAQ)",
      current: fix.current_schema || "None",
      suggested: typeof fix.suggested_schema === "string"
        ? fix.suggested_schema
        : JSON.stringify(fix.suggested_schema, null, 2),
    });
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-dark-300 border border-dark-50 rounded-xl max-w-2xl w-full max-h-[80vh] overflow-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-dark-50">
          <h3 className="text-sm font-semibold text-gray-200">
            {isContentBoost ? "Content Boost Preview" : "CTR Fix Preview"}
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-dark-50/50">
          <span className="text-xs text-gray-500">Page:</span>
          <span className="text-xs text-gray-300 font-mono ml-2">{insight.page_url}</span>
        </div>

        <div className="p-5 space-y-4">
          {fields.map(({ label, current, suggested, isContentBlock }) => (
            <div key={label}>
              <div className="text-xs font-medium text-gray-400 mb-2">{label}</div>
              {isContentBlock ? (
                <div className="bg-dark-50/50 rounded-lg p-4 border border-green-900/30">
                  <div className="text-[10px] uppercase tracking-wider text-green-400/80 mb-2">New Paragraph</div>
                  <div className="text-sm text-gray-200 leading-relaxed whitespace-pre-wrap">{suggested || "\u2014"}</div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-dark-50/50 rounded-lg p-3">
                    <div className="text-[10px] uppercase tracking-wider text-red-400/80 mb-1">Current</div>
                    <div className="text-xs text-gray-300 whitespace-pre-wrap break-words">{current || "\u2014"}</div>
                  </div>
                  <div className="bg-dark-50/50 rounded-lg p-3 border border-green-900/30">
                    <div className="text-[10px] uppercase tracking-wider text-green-400/80 mb-1">Suggested</div>
                    <div className="text-xs text-gray-200 whitespace-pre-wrap break-words">{suggested || "\u2014"}</div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-dark-50">
          <button
            onClick={onReject}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-400 hover:text-white border border-dark-50 rounded-lg transition-colors"
          >
            <XCircle size={14} />
            Reject
          </button>
          <button
            onClick={onApprove}
            className="flex items-center gap-2 px-4 py-2 bg-copper hover:bg-copper-600 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Check size={14} />
            {isContentBoost ? "Approve & Publish" : "Approve & Apply"}
          </button>
        </div>
      </div>
    </div>
  );
}
